import { useEffect, useState } from 'react';
import { THEMES, themeCssVars } from '@numslearn/shared';
import { SettingsProvider, useSettings } from './settings.js';
import { ProfileProvider, useProfile } from './profile.js';
import { MediaProvider } from './media.js';
import { useRoute, navigate, type Route } from './router.js';
import { GameScreen } from './game/GameScreen.js';
import { TrophyCase } from './game/TrophyCase.js';
import { AdminPanel } from './admin/AdminPanel.js';
import { Home } from './screens/Home.js';
import { Join } from './screens/Join.js';
import { PlayLive } from './screens/PlayLive.js';
import { Host } from './screens/Host.js';
import { Teacher } from './screens/Teacher.js';
import { useSession } from './session/useSession.js';

/**
 * Five screens behind one theme.
 *
 * The live-session hook is mounted for /join and /play together, so joining and
 * playing are one continuous connection rather than two.
 */
function Shell() {
  const { themeId, mode, rtl, t, num } = useSettings();
  const { counts } = useProfile();
  const [route] = useRoute();
  const [adminOpen, setAdminOpen] = useState(false);
  const [trophyOpen, setTrophyOpen] = useState(false);

  // The whole app is painted from one theme's tokens, set on :root.
  useEffect(() => {
    const vars = themeCssVars(THEMES[themeId], mode, rtl);
    for (const [k, v] of Object.entries(vars)) document.documentElement.style.setProperty(k, v);
  }, [themeId, mode, rtl]);

  const live = route.name === 'join' || route.name === 'play';

  return (
    <div className="app" data-art={themeId} data-route={route.name}>
      {route.name !== 'host' && (
        <nav className="topbar">
          <button className="wordmark" onClick={() => navigate('/')}>{t('app.name')}</button>
          <span className="topbar-acts">
            {(route.name === 'practice' || route.name === 'play') && (
              <button className="tb-btn" onClick={() => setTrophyOpen(true)} aria-haspopup="dialog">
                <span aria-hidden="true">🏅</span> {t('nav.badges')}
                <b className="tb-count">{num(counts.held)}</b>
              </button>
            )}
            {route.name === 'practice' && (
              <button className="tb-btn" onClick={() => setAdminOpen(true)} aria-haspopup="dialog">
                {t('nav.admin')}
              </button>
            )}
          </span>
        </nav>
      )}

      {route.name === 'home' && <Home />}
      {route.name === 'practice' && <GameScreen />}
      {route.name === 'teacher' && <Teacher />}
      {route.name === 'host' && <Host code={route.code} />}
      {live && <LiveRoutes route={route} />}

      {trophyOpen && <TrophyCase onClose={() => setTrophyOpen(false)} />}
      {adminOpen && <AdminPanel onClose={() => setAdminOpen(false)} />}
    </div>
  );
}

/** One mounted hook across both session screens: joining flows straight to play. */
function LiveRoutes({ route }: { route: Route }) {
  const session = useSession();
  useEffect(() => {
    // A returning player who still has a live session skips the join form.
    if (route.name === 'join' && session.status === 'joined') navigate('/play');
  }, [route.name, session.status]);

  if (route.name === 'join' && session.status !== 'joined') {
    return <Join code={route.code} session={session} />;
  }
  return <PlayLive session={session} />;
}

export default function App() {
  return (
    <SettingsProvider>
      <ProfileProvider>
        <MediaProvider>
          <Shell />
        </MediaProvider>
      </ProfileProvider>
    </SettingsProvider>
  );
}
